import { useCallback, useEffect, useState } from 'react';
import {
  Anchor,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  NativeSelect,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { Link } from 'react-router-dom';
import { api, errorMessage } from '../api/client';
import type { Webhook, WebhookDelivery, WebhookDeliveryStatus } from '../api/types';
import { whenLabel } from '../format';
import { ErrorNote } from './ErrorNote';
import { Loading } from './Loading';
import { ReadEdit } from './ReadEdit';
import { ReadList, ReadRow } from './ReadValue';
import { SecretOnce } from './SecretOnce';

/** What the address form holds while it is open. */
interface WebhookDraft {
  readonly url: string;
  readonly active: boolean;
}

/**
 * Where this publisher posts its newsletter signups, and what has happened to them.
 *
 * One webhook and one secret for the org, and deliberately none per brand. A brand is a masthead
 * sharing the publisher's data boundary, so a webhook per title would be isolation that isolates
 * nothing; what travels per masthead is the payload, and every delivery carries the brand it came
 * from for a receiver running several titles through one endpoint.
 *
 * Three things here are immediate and irreversible, so all three say what they do before they do
 * it: the secret is shown once at creation, rotating replaces it there and then with no overlap,
 * and removing the webhook stops every future delivery. All three are an owner's, because a
 * webhook decides where a publisher's reader data is sent, so an editor is not offered them at all
 * rather than refused by the server afterwards.
 *
 * The delivery list opens on the dead letters on purpose. A dead letter is a subscriber the
 * publisher's own system never heard about, which is the one thing here that is invisible unless
 * somebody goes looking, and the rest is just a log.
 *
 * The address is read until somebody presses Edit, like every other settings screen. The secret is
 * read only in a stronger sense: it was handed over once, at the moment it was made, and only a
 * hash of it was kept, so the read view says that it exists and when, and never shows a value or
 * anything shaped like one.
 */
export function WebhookSettings({
  orgId,
  orgName,
  canEdit,
}: {
  orgId: string;
  orgName: string;
  /** True for an owner. Everybody else reads the screen and is offered nothing on it. */
  canEdit: boolean;
}) {
  const [webhook, setWebhook] = useState<Webhook | null | undefined>(undefined);
  const [loadProblem, setLoadProblem] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const [secret, setSecret] = useState<{ value: string; rotated: boolean } | null>(null);
  const [rotating, setRotating] = useState(false);
  const [confirming, setConfirming] = useState<'rotate' | 'remove' | null>(null);
  const [removing, setRemoving] = useState(false);
  // True while the address form is open. Both things under it act on the webhook as it is saved,
  // so leaving them live while somebody is typing a new address is offering a way to lose it.
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => {
    setLoadProblem(null);
    api
      .orgWebhook(orgId)
      .then((body) => setWebhook(body.webhook))
      .catch((error: unknown) => setLoadProblem(errorMessage(error)));
  }, [orgId]);

  useEffect(load, [load]);

  async function save(draft: WebhookDraft): Promise<boolean> {
    setProblem(null);
    try {
      const body = await api.saveOrgWebhook(orgId, { url: draft.url.trim(), active: draft.active });
      setWebhook(body.webhook);
      if (body.secret !== undefined) setSecret({ value: body.secret, rotated: false });
      notifications.show({ message: 'Webhook saved.', color: 'violet' });
      return true;
    } catch (error) {
      setProblem(errorMessage(error));
      return false;
    }
  }

  async function rotate() {
    setProblem(null);
    setRotating(true);
    try {
      const body = await api.rotateOrgWebhookSecret(orgId);
      setWebhook(body.webhook);
      setSecret({ value: body.secret, rotated: true });
      setConfirming(null);
    } catch (error) {
      setProblem(errorMessage(error));
      setConfirming(null);
    } finally {
      setRotating(false);
    }
  }

  async function remove() {
    setProblem(null);
    setRemoving(true);
    try {
      await api.deleteOrgWebhook(orgId);
      setWebhook(null);
      setSecret(null);
      setConfirming(null);
      notifications.show({ message: 'Webhook removed.', color: 'violet' });
    } catch (error) {
      setProblem(errorMessage(error));
      setConfirming(null);
    } finally {
      setRemoving(false);
    }
  }

  return (
    <>
      <Title order={1} size="h2">
        Newsletter webhook
      </Title>
      <Text size="lg" mt="sm" mb="lg">
        Every signup on any brand under {orgName} is posted to this address as it happens, signed
        with a secret so the receiving end can tell it came from us. Confirmations and unsubscribes
        are posted too, so your own list can stay in step with ours. There is one address and one
        secret for the whole publisher: each delivery names the brand the reader signed up on, which
        is what to fan out on if your mastheads go to different lists.
      </Text>

      {!canEdit && (
        <Text c="dimmed" size="sm" mb="md">
          Only an owner can set this address, rotate its secret or remove it, because it decides
          where every reader who signs up is sent. Ask one of the owners of {orgName} if it needs to
          change.
        </Text>
      )}

      {loadProblem !== null && <ErrorNote message={loadProblem} onRetry={load} />}
      {loadProblem === null && webhook === undefined && <Loading label="Loading this webhook" />}

      {secret !== null && (
        <SecretOnce
          title={secret.rotated ? 'The new signing secret' : 'The signing secret'}
          value={secret.value}
          instruction={
            secret.rotated
              ? 'Put it into the receiving end now. The old secret stopped verifying the moment this one was made, so deliveries will fail their signature check until it is in place.'
              : 'Put it into whatever receives these deliveries. It verifies an HMAC-SHA256 signature over the timestamp and the body, and we cannot show it to you again.'
          }
          onDismiss={() => setSecret(null)}
        />
      )}

      {problem !== null && <ErrorNote message={problem} />}

      {webhook !== undefined && (
        <>
          {webhook === null && !canEdit ? (
            <Text c="dimmed" mt="sm">
              Nothing is set up, so no signup is posted anywhere. They are still collected, and the
              subscribers screen and the API keys are the other two ways to get at them.
            </Text>
          ) : (
            <Card withBorder radius="lg" p="lg" component="section">
              <ReadEdit<WebhookDraft>
                noun={webhook === null ? 'the new webhook' : 'the webhook address'}
                canEdit={canEdit}
                open={() => ({ url: webhook?.url ?? '', active: webhook?.active ?? true })}
                openLabel={webhook === null ? 'Set up a webhook' : 'Edit'}
                openName={webhook === null ? 'Set up a webhook' : 'Edit the webhook address'}
                saveLabel={webhook === null ? 'Save webhook' : 'Save address'}
                saveName={webhook === null ? 'Save webhook' : 'Save address'}
                onDraft={(draft) => setEditing(draft !== null)}
                check={(draft) =>
                  draft.url.trim().startsWith('https://')
                    ? []
                    : [
                        'The address has to start with https://. A signup carries a reader’s email address and the wording they agreed to, so it is never posted over plain http.',
                      ]
                }
                save={save}
                form={(draft, set) => (
                  <Stack gap="md">
                    <TextInput
                      id="webhook-url"
                      label="Where to post signups"
                      description="An https address on your own systems. Plain http is refused rather than upgraded."
                      placeholder="https://example.co.uk/hooks/quizee"
                      value={draft.url}
                      maxLength={2048}
                      onChange={(event) => set({ ...draft, url: event.currentTarget.value })}
                    />
                    <Checkbox
                      id="webhook-active"
                      label="Send signups to this address"
                      description="Turn it off to stop deliveries without losing the address or the secret."
                      checked={draft.active}
                      onChange={(event) => set({ ...draft, active: event.currentTarget.checked })}
                    />
                  </Stack>
                )}
              >
                {webhook === null ? (
                  <Text c="dimmed">
                    Nothing is set up, so no signup is posted anywhere. They are still collected,
                    and the subscribers screen and the API keys are the other two ways to get at
                    them.
                  </Text>
                ) : (
                  <ReadList>
                    <ReadRow label="Where to post signups" value={webhook.url} />
                    <ReadRow
                      label="Sending"
                      value={
                        webhook.active
                          ? 'On, every signup is posted as it happens'
                          : 'Off, nothing is being posted'
                      }
                      note={
                        webhook.active
                          ? undefined
                          : 'The address and the secret are kept, so turning it back on needs nothing at the receiving end.'
                      }
                    />
                    {/*
                     * What exists rather than what it is. The secret was handed over once, at the
                     * moment it was made, and only a hash of it was kept, so there is nothing here
                     * to show and nothing that should look like a value somebody could copy.
                     */}
                    <ReadRow
                      label="Signing secret"
                      value={`Made with this webhook on ${whenLabel(webhook.created_at)}`}
                      note="It was shown once, when it was made, and cannot be read back. If it has been lost, rotate it and put the new one into the receiving end."
                    />
                    <ReadRow
                      label="Deliveries"
                      value={
                        webhook.last_success_at === null
                          ? 'Nothing has been delivered successfully yet'
                          : `Last delivered ${whenLabel(webhook.last_success_at)}`
                      }
                      note={
                        webhook.last_failure_at === null
                          ? undefined
                          : `Last failure ${whenLabel(webhook.last_failure_at)}.`
                      }
                    />
                  </ReadList>
                )}
              </ReadEdit>

              {canEdit && webhook !== null && confirming === null && !editing && (
                <Group mt="md">
                  <Button
                    variant="default"
                    onClick={() => {
                      setProblem(null);
                      setConfirming('rotate');
                    }}
                  >
                    Rotate the secret
                  </Button>
                  <Button
                    variant="subtle"
                    color="red"
                    onClick={() => {
                      setProblem(null);
                      setConfirming('remove');
                    }}
                  >
                    Remove the webhook
                  </Button>
                </Group>
              )}

              {confirming === 'rotate' && (
                <Card withBorder radius="md" p="md" mt="md">
                  <Text size="sm" fw={500}>
                    Rotate the signing secret for {orgName}?
                  </Text>
                  <Text size="sm" mt={4}>
                    The new secret replaces the old one immediately and there is no overlap, which
                    is the whole point of a rotation. Until you have pasted the new one into the
                    receiving end, every delivery it gets will fail its signature check, for every
                    brand. The new secret is shown once, here, so have somewhere to put it ready.
                  </Text>
                  <Group mt="sm">
                    <Button
                      color="red"
                      size="compact-sm"
                      disabled={rotating}
                      onClick={() => void rotate()}
                      autoFocus
                    >
                      {rotating ? 'Rotating...' : 'Yes, rotate it now'}
                    </Button>
                    <Button
                      variant="default"
                      size="compact-sm"
                      disabled={rotating}
                      onClick={() => setConfirming(null)}
                    >
                      Leave it alone
                    </Button>
                  </Group>
                </Card>
              )}

              {confirming === 'remove' && (
                <Card withBorder radius="md" p="md" mt="md">
                  <Text size="sm" fw={500}>
                    Remove the webhook for {orgName}?
                  </Text>
                  <Text size="sm" mt={4}>
                    Nothing more is posted to your systems from the moment you press this, for any
                    brand, and the secret goes with it. Signups carry on being collected and stay on
                    this publisher&apos;s list, so nothing is lost: they just stop arriving anywhere
                    else. Setting one up again means a new address and a new secret.
                  </Text>
                  <Group mt="sm">
                    <Button
                      color="red"
                      size="compact-sm"
                      disabled={removing}
                      onClick={() => void remove()}
                      autoFocus
                    >
                      {removing ? 'Removing...' : 'Yes, remove it'}
                    </Button>
                    <Button
                      variant="default"
                      size="compact-sm"
                      disabled={removing}
                      onClick={() => setConfirming(null)}
                    >
                      Keep it
                    </Button>
                  </Group>
                </Card>
              )}
            </Card>
          )}

          {webhook !== null && <Deliveries orgId={orgId} />}
        </>
      )}
    </>
  );
}

/**
 * What has been posted, and what was given up on.
 *
 * Five attempts over about seven hours and then a delivery is dead, and a dead one is a subscriber
 * whose details never reached the publisher's own system. They are what this list opens on. There
 * is nothing to press on one: nothing can be sent again, so the way forward a dead letter offers
 * is the subscribers screen, where the same signups can be read or taken as a CSV. Their addresses
 * are not here either: a delivery record points at a subscriber, and the vault holds the address.
 */
function Deliveries({ orgId }: { orgId: string }) {
  const [status, setStatus] = useState<'dead' | 'all'>('dead');
  const [deliveries, setDeliveries] = useState<readonly WebhookDelivery[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [brandNames, setBrandNames] = useState<Readonly<Record<string, string>>>({});

  const load = useCallback(() => {
    setProblem(null);
    setDeliveries(null);
    const request =
      status === 'dead' ? api.webhookDeliveries(orgId, 'dead') : api.webhookDeliveries(orgId);
    request
      .then((body) => setDeliveries(body.deliveries))
      .catch((error: unknown) => setProblem(errorMessage(error)));
  }, [orgId, status]);

  useEffect(load, [load]);

  /**
   * The mastheads, so a delivery can say which one it came from in words.
   *
   * A failure here is deliberately silent: the id is still shown, and a list of deliveries is not
   * worth an error note about the brand names beside them.
   */
  useEffect(() => {
    let live = true;
    api
      .brands(orgId)
      .then((body) => {
        if (!live) return;
        setBrandNames(Object.fromEntries(body.brands.map((brand) => [brand.brand_id, brand.name])));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [orgId]);

  return (
    <>
      <Group justify="space-between" align="flex-end" gap="md" mt="xl" wrap="wrap">
        <div>
          <Title order={2} size="h3">
            Deliveries
          </Title>
          <Text size="sm" c="dimmed" mt={4}>
            A delivery is retried five times over about seven hours. After that it is a dead letter:
            a signup your system never received. Nothing can be posted again, so the way to pick one
            up is the{' '}
            <Anchor component={Link} to="/newsletters" inherit>
              subscribers screen
            </Anchor>
            , where the same signups can be read or taken as a CSV.
          </Text>
        </div>
        <NativeSelect
          id="webhook-delivery-filter"
          aria-label="Which deliveries to show"
          value={status}
          onChange={(event) => setStatus(event.target.value === 'all' ? 'all' : 'dead')}
          data={[
            { value: 'dead', label: 'Dead letters' },
            { value: 'all', label: 'Everything recent' },
          ]}
          style={{ minWidth: '12rem' }}
        />
      </Group>

      {problem !== null && <ErrorNote message={problem} onRetry={load} />}
      {problem === null && deliveries === null && <Loading label="Loading deliveries" />}

      {deliveries !== null && deliveries.length === 0 && (
        <Text c="dimmed" size="sm" mt="md">
          {status === 'dead'
            ? 'No dead letters. Every signup posted so far has been received.'
            : 'Nothing has been posted to this address yet.'}
        </Text>
      )}

      {deliveries !== null && deliveries.length > 0 && (
        <Stack component="ul" gap="xs" mt="md" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {deliveries.map((delivery) => (
            <Card component="li" key={delivery.delivery_id} withBorder radius="md" p="md">
              <Group gap="xs" align="center" wrap="wrap">
                <DeliveryBadge status={delivery.status} />
                <Text size="sm" fw={500}>
                  {delivery.event}
                </Text>
                <Text size="sm" c="dimmed">
                  {whenLabel(delivery.created_at)}
                </Text>
              </Group>
              <Text size="sm" c="dimmed" mt={4}>
                {brandNames[delivery.brand_id] ?? delivery.brand_id}
                {'. '}
                {delivery.attempts === 1 ? '1 attempt' : `${String(delivery.attempts)} attempts`}
                {delivery.last_error !== null && `, last error: ${delivery.last_error}`}
              </Text>
              <Text size="xs" c="dimmed" mt={2}>
                Subscriber {delivery.subscription_id}
              </Text>
            </Card>
          ))}
        </Stack>
      )}
    </>
  );
}

function DeliveryBadge({ status }: { status: WebhookDeliveryStatus }) {
  const colour = status === 'delivered' ? 'violet' : status === 'dead' ? 'red' : 'gray';
  const label =
    status === 'delivered'
      ? 'Delivered'
      : status === 'dead'
        ? 'Dead letter'
        : status === 'pending'
          ? 'Waiting to be posted'
          : 'Dropped';
  return (
    <Badge size="sm" radius="sm" variant="light" color={colour} tt="none">
      {label}
    </Badge>
  );
}
